import { Button, Form, Image, message } from 'antd';
import Title from 'antd/lib/typography/Title';
import { useParams } from "react-router-dom"
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import BookyBox from '../../Components/Layout/BookyBox';
import { TextService } from '../../Services/TextService';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import PagePublicSite from '../../Components/Page/PagePublicSite';
import { WebApiCompanyDtoCompanyPublicSiteDto, WebApiMemberDtoCustomMemberFieldValueDto, WebApiMemberDtoGetMemberResultDto, WebApiModelsMemberField } from '../../Repository/eventbookyapi';
import NotFound from '../../Components/Page/NotFoundPage';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { PublicResultType } from './Types/PublicResultType';
import { PublicSiteService } from '../../Services/PublicSiteService';
import { getMemberFieldSetting } from '../../Helpers/MemberFieldsHelper';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { getCurrentYearAsString } from '../../Helpers/DateHelpers';
import { OutPutMemberEditFields } from '../../Components/Forms/OutPutMemberFields';
import dayjs from 'dayjs';
import { PublicSiteMenu } from '../../Components/PublicSite/PublicSiteMenu';

const PublicSiteEditMember = () => {

    enum PublicSiteState {
        Undefined,
        CompanyNotFound,
        MemberFound
    }


    const [publicSite, setPublicSite] = useState([] as WebApiCompanyDtoCompanyPublicSiteDto);
    const [memberFieldSettings, setMemberFieldSettings] = useState([] as WebApiModelsMemberField[]);
    const [state, setState] = useState(PublicSiteState.Undefined as PublicSiteState);
    const [hasPaidCurrentYear, setHasPaidCurrentYear] = useState(false as boolean);
    const [showPaymentStatus, setShowPaymentStatus] = useState(false as boolean);

    let params = useParams();
    const navigation = useNavigate();
    const [form] = Form.useForm();
    const GetText = TextService.GetText;

    useEffect(() => {
        const loadData = async () => {
            if (!params.companyurl) {
                setState(PublicSiteState.CompanyNotFound);
                return;
            }

            try {
                const company = await PublicSiteService.GetCompanyPublicSiteByUrl(params.companyurl);
                setPublicSite(company);

                const memberFields = await PublicSiteService.GetMemberFieldsByUrl(params.companyurl);
                const field = getMemberFieldSetting(MemberFieldTypes.Payment, memberFields, null);
                setShowPaymentStatus(field.visiblePublic!);

                const filtered = memberFields.filter(f => f.columnType !== MemberFieldTypes.Payment);
                setMemberFieldSettings(filtered);

                const memberData = await PublicSiteService.GetMember();
                populateForm(memberData, filtered);
                setState(PublicSiteState.MemberFound);
            } catch {
                setState(PublicSiteState.CompanyNotFound);
            }
        };

        loadData();
    }, [params.companyurl]);

    const populateForm = (result: WebApiMemberDtoGetMemberResultDto, fields: WebApiModelsMemberField[]) => {
        form.setFieldValue("firstName", result.firstName);
        form.setFieldValue("lastName", result.lastName);
        form.setFieldValue("phoneNumber", result.phone);
        form.setFieldValue("email", result.email);
        form.setFieldValue("email2", result.email2);
        form.setFieldValue("address", result.streetAdress);
        form.setFieldValue("postalCode", result.postalCode);
        form.setFieldValue("postalArea", result.postalArea);
        form.setFieldValue("adressRow2", result.adressRow2);
        form.setFieldValue("adressRow3", result.adressRow3);
        form.setFieldValue("personalNumber", result.personalNumber);
        form.setFieldValue("webPage", result.webPage);
        form.setFieldValue("joinedDate", result.dateJoined ? dayjs(result.dateJoined) : null);
        form.setFieldValue("uniqueId", result.uniqueId);
        form.setFieldValue("company", result.company);
        form.setFieldValue("country", result.country);
        form.setFieldValue("title", result.title);
        form.setFieldValue("extra1", result.extra1);
        form.setFieldValue("extra2", result.extra2);
        form.setFieldValue("extra3", result.extra3);
        form.setFieldValue("emailNotificationPreference", result.emailNotificationPreference == 1 ? true : false);
        setHasPaidCurrentYear(result.hasPaidCurrentYear!);

        // Populate custom fields from customFieldsJson
        if (result.customFieldsJson) {
            try {
                const customFields = JSON.parse(result.customFieldsJson);
                if (Array.isArray(customFields)) {
                    customFields.forEach((field: any) => {
                        if (field && field.id !== undefined && field.value !== undefined) {
                            const fieldDef = fields?.find((f: any) => f.id === field.id);
                            if (fieldDef && fieldDef.uiControl === 1) {
                                const isValidDate = (val: any) => {
                                    if (!val) return false;
                                    if (typeof val === 'string') {
                                        return /^\d{4}-\d{2}-\d{2}$/.test(val) || !isNaN(Date.parse(val));
                                    }
                                    if (val instanceof Date && !isNaN(val.getTime())) return true;
                                    return false;
                                };
                                if (isValidDate(field.value)) {
                                    let dateVal = field.value;
                                    if (typeof dateVal === 'string') {
                                        dateVal = require('dayjs')(dateVal);
                                    } else if (dateVal instanceof Date) {
                                        dateVal = require('dayjs')(dateVal);
                                    }
                                    form.setFieldValue(field.id.toString(), dateVal);
                                }
                            } else {
                                form.setFieldValue(field.id.toString(), field.value);
                            }
                        }
                    });
                }
            } catch (e) {
                // ignore parse errors
            }
        }
    };

    const getDownloadUrl = (dto: WebApiCompanyDtoCompanyPublicSiteDto) => {
        return process.env.REACT_APP_API_URL + '/Image/downloadImage?bloburl=' + dto.siteLogoPath;
    }

    const MemberCreateClick = async (formValues: any) => {
        // Build customFields array like in EditMember
        const customFields = [] as WebApiMemberDtoCustomMemberFieldValueDto[];
        memberFieldSettings.forEach((field: WebApiModelsMemberField) => {
            if (field.columnType === MemberFieldTypes.Custom) {
                customFields.push({
                    fieldId: field.id!,
                    fieldValue: formValues[field.id!]
                });
            }
        });
        let result;
        try {
            result = await PublicSiteService.UpdateMember({
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                email: formValues.email,
                email2: formValues.email2,
                phoneNumber: formValues.phoneNumber,
                phoneNumber2: formValues.phoneNumber2,
                address: formValues.address,
                postalArea: formValues.postalArea,
                postalCode: formValues.postalCode,
                adressRow2: formValues.adressRow2,
                adressRow3: formValues.adressRow3,
                personalNumber: formValues.personalNumber,
                webPage: formValues.webPage,
                dateJoined: formValues.joinedDate,
                country: formValues.country,
                company: formValues.company,
                title: formValues.title,
                extra1: formValues.extra1,
                extra2: formValues.extra2,
                extra3: formValues.extra3,
                emailNotificationPreference: formValues.emailNotificationPreference ? 1 : 0,
                customFields: customFields
            });
        } catch (error) {
            message.warning(GetText("PublicSiteEditMember_Error"));
            return;
        }

        // Email changes go through a confirm-by-link flow sent to the new address, so the
        // login email isn't updated yet — route to a different success message for that case.
        const successType = result?.emailChangePending ? PublicResultType.EmailChangeConfirmationSent : PublicResultType.DetailsHasBeenSaved;
        navigation("/" + getCurrentLanguage() + "/" + params.companyurl + "/success/" + successType)
    }


    if (state === PublicSiteState.CompanyNotFound) {
        return <NotFound message={GetText("PublicSiteEditMember_CompanyNotFound")} />
    }

    const onPhoneChange = (value: any) => {
        form.setFieldValue("phoneNumber", value);
    }

    return <PagePublicSite>
        {state === PublicSiteState.MemberFound && (
            <>
                {publicSite.siteLogoPath ? (
                    <div style={{ marginBottom: "20px", textAlign: "center" }}>
                        <Image preview={false} className='company-logo' src={getDownloadUrl(publicSite)} />
                    </div>
                ) : (
                    <div style={{ textAlign: "center", marginBottom: "20px" }}>
                        <Title style={{ margin: 0 }}>{publicSite.siteTitle}</Title>
                    </div>
                )}
                <PublicSiteMenu />
            </>
        )}
        <BookyBox>

            {state === PublicSiteState.MemberFound ? <h2>{GetText("PublicSiteEditMemberEditMemberTitle")}</h2> : ""}

            {state === PublicSiteState.MemberFound &&
                publicSite.siteDescription &&
                publicSite.siteDescription.length > 0 ? <p dangerouslySetInnerHTML={{ __html: publicSite.siteDescription! }}></p> : ""}

            {state === PublicSiteState.MemberFound && showPaymentStatus ? <div style={{ paddingTop: "10px", paddingBottom: "10px" }}>
                {hasPaidCurrentYear ? <span><CheckCircleOutlined style={{ color: "green" }} /> Medlemsavgift är betald för {getCurrentYearAsString()}</span> : ""}
                {!hasPaidCurrentYear ? <span><CloseCircleOutlined style={{ color: "red" }} /> Medlemsavgift har inte registrerats som betald för {getCurrentYearAsString()}</span> : ""}<br />
            </div> : ""}

            {state === PublicSiteState.MemberFound ? <Form
                data-testid="create-form"
                name="basic"
                form={form}
                colon={false}
                onFinish={MemberCreateClick}
                layout="vertical"
                initialValues={{
                    remember: true,
                }}
                autoComplete="off"
            >
                <OutPutMemberEditFields
                    memberFieldSettings={memberFieldSettings}
                    membershipProducts={[]}
                    GetText={GetText}
                    groups={[]} // Pass groups if needed, or [] if not used
                    user={{ hasOnlyReadRights: false }} // Pass user object if needed, or a minimal object
                    onPhoneChange={onPhoneChange}
                    isAdmin={false}
                />

                <Form.Item
                    label=""
                    name="submit"
                    rules={[
                        {
                            required: false,
                            message: 'submitbutton',
                        },
                    ]}
                >
                    <Button data-testid="submit-button" className='saveButton' size="middle" type="primary" htmlType="submit">
                        {GetText("EditMember_ModalOk")}
                    </Button>
                </Form.Item>
            </Form> : ""}
        </BookyBox>
    </PagePublicSite>
}

export default PublicSiteEditMember;